// Washington State University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - WSU's Common Data Set 2025-2026 (CDS_2025-2026-PDF_For-Website.pdf),
//    linked from WSU Data and Information's Official Reporting page: C1
//    applicants, admits and enrollees for Fall 2025 with the residency table,
//    C8A (WSU does not use SAT or ACT scores in admission decisions), C14
//    (March 31 priority date), C21/C22 (no early decision, no early action),
//    G1/G5 2026-2027 costs. The PDF's fonts garble some labels and check
//    marks; the marks were read from their positions beside each box.
//  - WSU Admissions: First-Year Students (application steps, the test policy)
//    and Admissions Dates & Deadlines 2026-2027 (the calendar for the
//    application that opened August 3, 2026, fall 2027 entry). Neither page
//    publishes decision dates.
//  - College Scorecard, school id 236939 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items for a first-year student living on campus; G1
// gives housing ($9,887) and food ($5,358) separately, which sum to its
// $15,245 food and housing figure.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'WSU Common Data Set 2025-2026',
        url: 'https://wpcdn.web.wsu.edu/wp-provost/uploads/sites/3285/2026/05/CDS_2025-2026-PDF_For-Website.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'WSU Admissions, First-Year Students',
        url: 'https://admission.wsu.edu/apply/first-year-students/',
        read: '2026-10-01',
    },
    dates: {
        label: 'WSU Admissions, Dates & Deadlines 2026-2027',
        url: 'https://admission.wsu.edu/apply/admissions-dates-deadlines/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?236939-Washington-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'washington-state-university',
    kbId: 'washington_state_university',
    name: 'Washington State University',
    shortName: 'Washington State',
    aliases: ['WSU', 'Wazzu'],
    location: 'Pullman, WA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (WSU brand colors page); decoration only.
    branding: {
        primary: '#A60F2D',
        secondary: '#4D4D4D',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/washington_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 26403,
        admitted: 22732,
        enrolled: 4420,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 15845, admitted: 13466 },
            { group: 'Out-of-state', applied: 9902, admitted: 8676 },
            { group: 'International', applied: 656, admitted: 590 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'WSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'WSU has no early decision or early action plan. Its March 31 date is a priority date for admission and automatic awards, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority date', value: 'March 31' },
        rows: [
            { plan: 'Top Scholars consideration', deadline: 'January 31', decision: null, binding: false },
            { plan: 'Fall priority date', deadline: 'March 31', decision: null, binding: false },
        ],
        note: 'The application opened August 3, 2026, through WSU\'s own application or the Common App; you only need one. Apply by March 31 to be eligible for automatic awards such as the University Achievement Award and the WUE/Cougar Award. After March 31, WSU accepts applications on a space-available basis. The General Scholarship application and the FAFSA or WASFA have a January 31 priority date. The application fee is $70. Confirm your enrollment by May 1. WSU does not publish decision dates.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'WSU does not require SAT or ACT scores and will not use them in admission decisions, so scores are not considered even if you send them.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also answers no when asked whether WSU uses SAT or ACT scores in admission decisions for first-year applicants.',
        cdsWhere: 'section C8A',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [12063, 29734] },
            { item: 'Required fees', amounts: [2298, 2298] },
            { item: 'Housing (on campus)', amounts: [9887, 9887] },
            { item: 'Food (on-campus meal plan)', amounts: [5358, 5358] },
            { item: 'Books and supplies', amounts: [900, 900] },
            { item: 'Transportation', amounts: [1716, 1716] },
            { item: 'Other expenses', amounts: [1968, 1968] },
        ],
        totals: [34190, 51861],
        note: 'For a full-time first-year student living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 13462,
            byIncome: [
                { income: '$0 to $30,000', amount: 6750 },
                { income: '$30,001 to $48,000', amount: 7495 },
                { income: '$48,001 to $75,000', amount: 8683 },
                { income: '$75,001 to $110,000', amount: 15212 },
                { income: 'Over $110,000', amount: 24537 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from WSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
